export const namespaced = true;

import TamaiClassService from '@/services/TamaiClassService.js';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import filter from 'lodash/filter';
import find from 'lodash/find';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { basicPresent, basicSubFieldPresent } from '@/presenters/fields';

export const state = {
  tamaiClasses: [],
  tamaiClass: {},
  editingTamaiClass: null,
};

export const mutations = {
  SET_TAMAI_CLASSES(state, tamaiClasses) {
    state.tamaiClasses = tamaiClasses;
  },
  CLEAR_TAMAI_CLASSES(state) {
    state.tamaiClasses = [];
  },
  SET_TAMAI_CLASS(state, tamaiClass) {
    state.tamaiClass = tamaiClass;
  },
  RESTORE_FORM_DATA(state, tamaiClass) {
    state.tamaiClass = tamaiClass;
  },
  SET_EDITING_TAMAI_CLASS(state, tamaiClass) {
    state.editingTamaiClass = tamaiClass;
  },
  DELETE_EDITING_TAMAI_CLASS(state) {
    state.editingTamaiClass = null;
  },
  UPDATE_TAMAI_CLASS(state, tamaiClass) {
    state.tamaiClass = tamaiClass;
  },
  ADD_TAMAI_CLASS(state, tamaiClass) {
    state.tamaiClass = tamaiClass;
  },
  FREEZE_TAMAI_CLASS() {
    // We only freeze Tamai Class, so we don't need to do anything here
  },
};

export const actions = {
  fetchTamaiClasses({ commit, getters }, { data }) {
    return TamaiClassService.all(data).then((response) => {
      const tamaiClasses = map(response.data, (tamaiClass) => {
        tamaiClass.mainTeacher = find(tamaiClass.teachers, (teacher) => teacher.type == 'main');

        const subTeachers = filter(tamaiClass.teachers, (teacher) => teacher.type == 'sub');
        for (let index = 0; index < getters.maxNumOfTeacher; index++) {
          const subTeacher = subTeachers[index];
          tamaiClass[`teacher${index + 1}`] = subTeacher || {};
        }

        return tamaiClass;
      });

      commit('SET_TAMAI_CLASSES', tamaiClasses);
    });
  },
  clearTamaiClasses({ commit }) {
    commit('CLEAR_TAMAI_CLASSES', []);
  },
  fetchTamaiClass({ commit, getters }, id) {
    return TamaiClassService.get(id).then((response) => {
      let tamaiClass = response.data;
      tamaiClass.mainTeacher = find(tamaiClass.teachers, (teacher) => teacher.type == 'main');

      const subTeachers = filter(tamaiClass.teachers, (teacher) => teacher.type == 'sub');
      for (let index = 0; index < getters.maxNumOfTeacher; index++) {
        const subTeacher = subTeachers[index];
        tamaiClass[`teacher${index + 1}`] = subTeacher || {};
      }

      commit('SET_TAMAI_CLASS', tamaiClass);
      return tamaiClass;
    });
  },
  restoreFormData({ commit }, formData) {
    commit('RESTORE_FORM_DATA', formData);
  },
  startEditingTamaiClass({ commit }, tamaiClass) {
    commit('SET_EDITING_TAMAI_CLASS', tamaiClass);
  },
  finishEditingTamaiClass({ commit, state, getters }, id) {
    if (state.editingTamaiClass) {
      let formattedTamaiClass = cloneDeep(state.editingTamaiClass);
      if (formattedTamaiClass.corporation) {
        formattedTamaiClass.corporationId = formattedTamaiClass.corporation.id;
        formattedTamaiClass = omit(formattedTamaiClass, ['corporation']);
      }
      if (formattedTamaiClass.brand) {
        formattedTamaiClass.brandId = formattedTamaiClass.brand.id;
        formattedTamaiClass = omit(formattedTamaiClass, ['brand']);
      }
      if (formattedTamaiClass.school) {
        formattedTamaiClass.schoolId = formattedTamaiClass.school.id;
        formattedTamaiClass = omit(formattedTamaiClass, ['school']);
      }
      if (formattedTamaiClass.course) {
        formattedTamaiClass.courseId = formattedTamaiClass.course.id;
        formattedTamaiClass = omit(formattedTamaiClass, ['course']);
      }
      if (formattedTamaiClass.mainTeacher) {
        formattedTamaiClass.mainTeacherId = formattedTamaiClass.mainTeacher.id;
        formattedTamaiClass = omit(formattedTamaiClass, ['mainTeacher']);
      }
      for (let index = 1; index <= getters.maxNumOfTeacher; index++) {
        let teacherIndex = 'teacher' + index;
        if (formattedTamaiClass[teacherIndex] && formattedTamaiClass[teacherIndex].id) {
          formattedTamaiClass[teacherIndex + 'Id'] = formattedTamaiClass[teacherIndex].id;
        } else {
          formattedTamaiClass[teacherIndex + 'Id'] = '';
        }
        formattedTamaiClass = omit(formattedTamaiClass, [teacherIndex]);
      }
      if (formattedTamaiClass.teachers) {
        formattedTamaiClass = omit(formattedTamaiClass, ['teachers']);
      }

      if (id) {
        return TamaiClassService.update(id, formattedTamaiClass).then((response) => {
          commit('DELETE_EDITING_TAMAI_CLASS');
          commit('UPDATE_TAMAI_CLASS', response.data);
        });
      } else {
        return TamaiClassService.create(formattedTamaiClass).then((response) => {
          commit('DELETE_EDITING_TAMAI_CLASS');
          commit('ADD_TAMAI_CLASS', response.data);
        });
      }
    }
  },
  clearEditingTamaiClass({ commit, state }) {
    if (state.editingTamaiClass) {
      commit('DELETE_EDITING_TAMAI_CLASS');
    }
  },
  addTamaiClass({ commit }, tamaiClass) {
    commit('ADD_TAMAI_CLASS', tamaiClass);
  },
  freezeTamaiClass({ commit }, payload) {
    let formattedFrozenData = {};
    if (payload.frozenMonth != 'now') {
      formattedFrozenData['frozenDatetime'] = payload.frozenMonth;
    }
    return TamaiClassService.frozen(payload.id, formattedFrozenData).then(() => {
      commit('FREEZE_TAMAI_CLASS');
    });
  },
};

export const getters = {
  getTamaiClassById: (state) => (id) => {
    return state.tamaiClasses.find((item) => item.id == id);
  },
  totalTamaiClasses: (state) => {
    return state.tamaiClasses.length;
  },
  maxNumOfTeacher() {
    // This is hardcode as max 8 teachers
    return 8;
  },
  getExcelFormattedTamaiClasses: (state, getters) => (fields) => {
    return map(state.tamaiClasses, (tamaiClass) => {
      let data = {
        法人名: basicSubFieldPresent(tamaiClass, 'corporation', 'name'),
        ブランド名: basicSubFieldPresent(tamaiClass, 'brand', 'name'),
        教場名: basicSubFieldPresent(tamaiClass, 'school', 'name'),
        コース名: basicSubFieldPresent(tamaiClass, 'course', 'name'),
        'クラスID（自動生成）': basicPresent(tamaiClass.uid),
        クラス名: basicPresent(tamaiClass.name),
        '先生名（担当・メイン）': basicSubFieldPresent(tamaiClass, 'mainTeacher', 'name'),
        クラス人数: tamaiClass.classSize.toString(),
      };

      for (let index = 1; index < getters.maxNumOfTeacher + 1; index++) {
        let teacherIndex = 'teacher' + index;
        if (tamaiClass[teacherIndex]) {
          data['先生名（担当・サブ' + index + '）'] = basicSubFieldPresent(tamaiClass, teacherIndex, 'name');
        }
      }

      return pick(data, fields);
    });
  },
  existsRegistrationRequiredParams: (state) => {
    if (!state.editingTamaiClass.name) return false;
    if (!state.editingTamaiClass.nameReading) return false;

    return true;
  },
  existsUpdateRequiredParams: (state) => {
    if (!state.editingTamaiClass.name) return false;
    if (!state.editingTamaiClass.nameReading) return false;

    return true;
  },
};
